import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Archive, RefreshCcw, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api } from "../../lib/api";

const CAMPS = [
  "El Aaiún (Laâyoune)",
  "Smara",
  "Ausserd (Aousserd)",
  "Dakhla",
  "Boujdour",
  "Rabouni",
];

const CAMP_ALL = "__all__";

function fmtDate(iso) {
  try {
    return new Intl.DateTimeFormat("es-ES", {
      weekday: "short",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function toLocalInputValue(iso) {
  // format YYYY-MM-DDTHH:mm for datetime-local input
  const d = iso ? new Date(iso) : new Date(Date.now() + 24 * 3600 * 1000);
  if (!iso) d.setHours(10, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(local) {
  return new Date(local).toISOString();
}

export default function AdminSalidas() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [status, setStatus] = useState("all");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(() => {
    const qp = status !== "all" ? `?status=${status}` : "";
    return api
      .get(`/admin/departures${qp}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0 }));
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setOpenForm(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setOpenForm(true);
  };

  const toggleArchive = async (row) => {
    const next = row.status === "active" ? "archived" : "active";
    try {
      await api.patch(`/admin/departures/${row.id}`, { status: next });
      toast.success(next === "archived" ? "Salida archivada" : "Salida activada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const deleteRow = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/admin/departures/${confirmDel.id}`);
      toast.success("Salida eliminada");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div data-testid="admin-salidas" className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-3xl font-black tracking-tight flex-1">Salidas</h1>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44" data-testid="admin-salidas-filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="active">Activas</SelectItem>
            <SelectItem value="archived">Archivadas</SelectItem>
          </SelectContent>
        </Select>
        <Button
          data-testid="admin-salidas-new-btn"
          onClick={openNew}
          className="rounded-full bg-[#00953B] hover:bg-[#007a30] text-white"
        >
          <Plus className="h-4 w-4 mr-1" /> Nueva salida
        </Button>
      </div>

      <div className="rounded-2xl border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-neutral-500 bg-neutral-50">
            <tr>
              <th className="p-3">Fecha</th>
              <th>Destino</th>
              <th>Notas</th>
              <th>Estado</th>
              <th>Creada</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((row) => (
              <tr
                key={row.id}
                data-testid={`admin-salida-row-${row.id}`}
                className="border-t align-top"
              >
                <td className="p-3 whitespace-nowrap">{fmtDate(row.date)}</td>
                <td>{row.destination_camp || "Todos los campamentos"}</td>
                <td className="max-w-xs">
                  <span className="text-neutral-600">
                    {row.notes
                      ? row.notes.length > 60
                        ? row.notes.slice(0, 60) + "…"
                        : row.notes
                      : "—"}
                  </span>
                </td>
                <td>
                  <Badge
                    className={
                      row.status === "active"
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-neutral-200 text-neutral-700"
                    }
                  >
                    {row.status === "active" ? "Activa" : "Archivada"}
                  </Badge>
                </td>
                <td className="text-xs text-neutral-500 whitespace-nowrap">
                  {new Date(row.created_at).toLocaleDateString("es-ES")}
                </td>
                <td className="pr-3 whitespace-nowrap">
                  <div className="flex gap-1 justify-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(row)}
                      data-testid={`admin-salida-edit-${row.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleArchive(row)}
                      data-testid={`admin-salida-toggle-${row.id}`}
                      title={row.status === "active" ? "Archivar" : "Activar"}
                    >
                      {row.status === "active" ? (
                        <Archive className="h-4 w-4" />
                      ) : (
                        <RefreshCcw className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmDel(row)}
                      data-testid={`admin-salida-delete-${row.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-neutral-500 text-sm">
                  No hay salidas con este filtro.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <SalidaForm
        open={openForm}
        onOpenChange={setOpenForm}
        editing={editing}
        onSaved={() => {
          setOpenForm(false);
          load();
        }}
      />

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="admin-salida-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta salida?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteRow}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="admin-salida-delete-confirm-btn"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SalidaForm({ open, onOpenChange, editing, onSaved }) {
  const isEdit = !!editing;
  const [date, setDate] = useState(toLocalInputValue(editing?.date));
  const [camp, setCamp] = useState(editing?.destination_camp || CAMP_ALL);
  const [notes, setNotes] = useState(editing?.notes || "");
  const [statusVal, setStatusVal] = useState(editing?.status || "active");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDate(toLocalInputValue(editing?.date));
    setCamp(editing?.destination_camp || CAMP_ALL);
    setNotes(editing?.notes || "");
    setStatusVal(editing?.status || "active");
    setError(null);
  }, [open, editing]);

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      const body = {
        date: fromLocalInputValue(date),
        destination_camp: camp === CAMP_ALL ? null : camp,
        notes: notes.trim() || null,
        status: statusVal,
      };
      if (isEdit) {
        await api.patch(`/admin/departures/${editing.id}`, body);
        toast.success("Salida actualizada");
      } else {
        await api.post(`/admin/departures`, body);
        toast.success("Salida creada");
      }
      onSaved();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error al guardar";
      setError(typeof msg === "string" ? msg : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-salida-form">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar salida" : "Nueva salida"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div>
            <Label>Fecha y hora</Label>
            <Input
              type="datetime-local"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="admin-salida-date"
              className="mt-1"
            />
            <p className="text-xs text-neutral-500 mt-1">Debe ser una fecha futura.</p>
          </div>
          <div>
            <Label>Destino</Label>
            <Select value={camp} onValueChange={setCamp}>
              <SelectTrigger data-testid="admin-salida-camp" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={CAMP_ALL}>Todos los campamentos</SelectItem>
                {CAMPS.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notas (opcional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 200))}
              maxLength={200}
              rows={3}
              className="mt-1"
              data-testid="admin-salida-notes"
            />
            <p className="text-xs text-neutral-500 mt-1">{notes.length}/200</p>
          </div>
          <div>
            <Label>Estado</Label>
            <Select value={statusVal} onValueChange={setStatusVal}>
              <SelectTrigger data-testid="admin-salida-status" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Activa</SelectItem>
                <SelectItem value="archived">Archivada</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {error && (
            <div
              data-testid="admin-salida-form-error"
              className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
            >
              {error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="admin-salida-save-btn"
            className="bg-[#00953B] hover:bg-[#007a30] text-white"
          >
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
